import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { CameraMode, IColor4, IMatrix4, IVector3 } from '../../api.js';
import { InteractionSystem } from '../InteractionSystem.js';
import { RenderView } from '../RenderView.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { Observable } from '../../Observable.js';
import { ViewerFramebufferType } from '../Framebuffer.js';
import '../shaders/blit.fragment.js';
const TEXTURE_SAMPLER = 'textureSampler';
export class ViewerXRSession {
    constructor(viewer, session, layer, reference_space) {
        this.on_end_observable = new Observable();
        this._position_offset = new IVector3(0, 0, 0);
        this._yaw_offset_matrix = IMatrix4.Identity();
        this._last_time = 0.0;
        this._elapsed_frames = 0;
        this._silence_no_viewer_pose_warning = false;
        this.viewer = viewer;
        this._xr = session;
        this._layer = layer;
        this._reference_space = reference_space;
        this.view = new RenderView(this.viewer, new InteractionSystem(this.viewer, false), this.viewer.get_canvas_render_width(), this.viewer.get_canvas_render_height(), ViewerFramebufferType.RGBA16F, 1);
        this.view.custom_clear_color = new IColor4(0, 0, 0, 0);
        this._to_canvas_post_process = new BABYLON_PostProcess('To_canvas_frame_buffer_and_beeeeeyond', 'blit', [], [
            TEXTURE_SAMPLER
        ], { width: 1, height: 1 }, new BABYLON_Camera('', new BABYLON_Vector3(), this.viewer.scene));
        this._to_canvas_post_process.forceFullscreenViewport = true;
        this._to_canvas_post_process.alphaMode = BABYLON_Constants.ALPHA_COMBINE;
        this._to_canvas_post_process.onApply = (effect) => {
            effect.setTexture(TEXTURE_SAMPLER, this.view.postprocess_manager.get_active_accumulation_buffer().accumulation_buffer2);
        };
        session.addEventListener('end', () => {
            this.end_session();
        });
        session.requestAnimationFrame((time, frame) => {
            this.render(time, frame);
        });
    }
    end_session() {
        this.on_end_observable.notify(this);
        this._xr.end();
        this.dispose();
    }
    get position_offset() {
        return this._position_offset;
    }
    set position_offset(offset) {
        this._position_offset.x = offset.x;
        this._position_offset.y = offset.y;
        this._position_offset.z = offset.z;
    }
    /**
     * Sets a rotation offset along the vertical axis.
     * @param yaw_offset_radians The angle, in radians, to use as offset
     */
    set_yaw_offset(yaw_offset_radians) {
        IMatrix4.copy_to_ref(IMatrix4.Rotation_Y(yaw_offset_radians), this._yaw_offset_matrix);
    }
    dispose() {
        this.on_end_observable.dispose();
        this.view.dispose();
    }
    render(time, frame) {
        this._xr.requestAnimationFrame((time, frame) => {
            this.render(time, frame);
        });
        const viewer_pose = frame.getViewerPose(this._reference_space);
        if (!viewer_pose) {
            if (this.viewer.get_config().verbose_warnings && !this._silence_no_viewer_pose_warning) {
                console.warn('No Viewer Pose given by XR frame, skipping this frame. This warning will only show once until a frame as been rendered again.');
                this._silence_no_viewer_pose_warning = true;
            }
            // We can't render without any viewer pose, as we don't have any views to render into.
            return;
        }
        if (this._layer !== frame.session.renderState.baseLayer) {
            console.error('Unexpected base render layer in XR session');
            return;
        }
        // Since we have a good state now, warn again if we don't have a Viewer pose in any upcoming frames.
        this._silence_no_viewer_pose_warning = false;
        for (let i = 0; i < viewer_pose.views.length; i++) {
            const view = viewer_pose.views[i];
            const viewport = this._layer.getViewport(view);
            this.view.set_render_size(viewport.width, viewport.height);
            const camera = this.view.engine_camera;
            const projection_matrix = IMatrix4.from_array(view.projectionMatrix);
            const view_transform = IMatrix4.from_array(view.transform.matrix);
            const xr_camera_matrix = view_transform;
            // Offset camera
            IVector3.add_in_place(xr_camera_matrix[3], this._position_offset);
            const view_matrix = IMatrix4.inverse(IMatrix4.multiply(xr_camera_matrix, this._yaw_offset_matrix));
            const dt = {
                milliseconds: time - this._last_time,
                seconds: (time - this._last_time) / 1000.0,
                total_time_seconds: time / 1000.0,
                total_time_milliseconds: time,
                elapsed_frames: this._elapsed_frames
            };
            camera.set_view_matrix(view_matrix);
            camera.set_projection_matrix(projection_matrix, CameraMode.PERSPECTIVE);
            this.view.update(dt);
            this.view.render();
            const gl = this.viewer.engine._gl;
            this.viewer.engine.bindFramebuffer(this.view.framebuffer.get_render_target().renderTarget);
            gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._layer.framebuffer);
            gl.viewport(0, 0, this._layer.framebufferWidth, this._layer.framebufferHeight);
            gl.blitFramebuffer(0, 0, viewport.width, viewport.height, viewport.x, viewport.y, viewport.x + viewport.width, viewport.y + viewport.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            this.viewer.engine.restoreDefaultFramebuffer();
        }
        this._elapsed_frames++;
        this._last_time = time;
    }
}
//# sourceMappingURL=ViewerXRSession.js.map